import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Descriptions, Space, Tag, Typography, message } from "antd";
import LoadingCard from "components/LoadingCard/LoadingCard";
import { CONTROL } from "pages/MarketingPages/FieldInput";
import { describeRebuild, formatMoment } from "./rebuildState";
import { errorMessage, fetchRebuildStatus, triggerRebuild } from "./siteUpdatesApi";

const { Title, Text } = Typography;

// Slow on purpose. The queue moves in minutes, so anything faster only adds
// requests to a panel that is meant to be left open on a second screen.
const POLL_MS = 30000;

/**
 * The one sentence the panel shows, keyed by `describeRebuild`'s `state.key`
 * (see rebuildState.js) — plain Bulgarian literals rather than react-i18next,
 * which this admin never actually wired up a Bulgarian locale for (only
 * `en` is loaded in src/i18n.js), so `t()` here was always showing English.
 */
const REBUILD_MESSAGES = {
  rebuild_state_off:
    "Автоматичното обновяване е изключено в тази среда. Публикуваните промени са запазени и ще се появят при следващото обновяване.",
  rebuild_state_pending: ({ time, minutes }) =>
    `Промените ще се появят на сайта до ${time} ч. — около ${minutes} мин.`,
  rebuild_state_pending_soon: "Промените ще се появят на сайта скоро.",
  rebuild_state_failed: "Последното обновяване не бе успешно. Промените не са на сайта.",
  rebuild_state_ok: ({ time }) => `Сайтът е обновен в ${time} ч.`,
  rebuild_state_idle: "Няма чакащи промени. Сайтът показва последната публикувана версия.",
};

const rebuildMessage = (state) => {
  const entry = REBUILD_MESSAGES[state.key];
  return typeof entry === "function" ? entry(state.values) : entry || "";
};

/** Content types the rebuild queue can name as a reason it is waiting. */
const REASON_LABELS = {
  FAQ: "Често задавани въпроси",
  testimonials: "Отзиви",
  posts: "Статии",
  "page copy": "Маркетингови страници",
  promotions: "Промоции",
  redirects: "Пренасочвания",
};

const reasonLabel = (reason) => REASON_LABELS[reason] || reason;

/**
 * Whether the public site is carrying what this admin has published.
 *
 * The landing site is a static Astro build that reads pages, posts, the FAQ
 * and testimonials from the API *at build time*, so publishing anywhere else
 * in this admin changes nothing visitors can see until a build runs. The API
 * debounces those requests and a cron starts the pipeline; both are invisible.
 *
 * That invisibility is what this screen is for. Without it a publish is
 * completely silent, and "the quiet window is still counting down" looks
 * exactly like "the build token expired three weeks ago" — minutes of nothing
 * happening in both cases. One sentence separates them here, and the button
 * below it skips the wait when the answer is "just do it now".
 */
function SiteUpdates() {
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isTriggering, setIsTriggering] = useState(false);
  const isMountedRef = useRef(true);

  // A poll or a trigger can still be in flight when the tab is navigated away
  // from, and its response must not land on a component that is gone.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const load = useCallback(async ({ silent } = {}) => {
    // A poll never shows the spinner: the panel sits open for minutes at a
    // time, and flashing it away every 30s makes a calm screen look busy.
    if (!silent) setIsLoading(true);
    try {
      const next = await fetchRebuildStatus();
      if (!isMountedRef.current) return;
      setStatus(next);
      setLoadError(null);
    } catch (error) {
      if (isMountedRef.current) setLoadError(errorMessage(error));
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(() => load({ silent: true }), POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const triggerNow = async () => {
    setIsTriggering(true);
    try {
      const result = await triggerRebuild();
      if (result?.ok) {
        message.success("Обновяването започна.");
      } else {
        // The API declined on purpose and said why. Calm, not red.
        message.info(result?.message || REBUILD_MESSAGES.rebuild_state_off);
      }
      await load({ silent: true });
    } catch (error) {
      message.error(errorMessage(error));
    } finally {
      if (isMountedRef.current) setIsTriggering(false);
    }
  };

  const state = describeRebuild(status);
  const lastResult = status?.lastResult;

  return (
    <div style={{ padding: 16, maxWidth: 720, margin: "0 auto" }}>
      <Space direction="vertical" size={4} style={{ width: "100%", marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Обновяване на сайта
        </Title>
        <Text type="secondary">
          Публикуваните промени стигат до посетителите едва след като сайтът се обнови.
          Тук виждаш кога ще стане това и дали последното обновяване е минало успешно.
        </Text>
      </Space>

      {isLoading && <LoadingCard />}

      {!isLoading && loadError && (
        <Alert
          type="error"
          showIcon
          message="Неуспешно зареждане на статуса на обновяването"
          description={loadError}
          action={<Button onClick={() => load()}>Опитай отново</Button>}
        />
      )}

      {!isLoading && !loadError && (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Alert
            type={state.tone}
            showIcon
            message={rebuildMessage(state)}
            description={
              (state.reasons.length > 0 || state.failure) && (
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  {state.reasons.length > 0 && (
                    <Space wrap size={4}>
                      <Text type="secondary">Чака се за:</Text>
                      {state.reasons.map((reason) => (
                        <Tag key={reason}>{reasonLabel(reason)}</Tag>
                      ))}
                    </Space>
                  )}
                  {state.failure && (
                    <Text type={state.state === "failed" ? undefined : "warning"}>
                      {state.state === "failed"
                        ? state.failure
                        : `Предишният опит не бе успешен: ${state.failure}`}
                    </Text>
                  )}
                </Space>
              )
            }
          />

          {/* Always offered, never pre-judged: an environment that will not
              build answers with a 409 and its own explanation, which is a
              better answer than a button greyed out for reasons of its own. */}
          <Button
            type="primary"
            block
            style={CONTROL}
            loading={isTriggering}
            disabled={isTriggering}
            onClick={triggerNow}
          >
            Обнови сайта сега
          </Button>

          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="Изгражда се от">
              <Text type="secondary">{status?.target || "—"}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Последно изграждане">
              <Text type="secondary">
                {status?.lastTriggeredAt ? formatMoment(status.lastTriggeredAt) : "—"}
                {lastResult?.buildNumber ? ` · #${lastResult.buildNumber}` : ""}
                {lastResult?.triggeredBy ? ` · ${lastResult.triggeredBy}` : ""}
              </Text>
            </Descriptions.Item>
          </Descriptions>
        </Space>
      )}
    </div>
  );
}

export default SiteUpdates;
